
import { useEffect, useRef, useState } from 'react';

interface SpinWheelProps {
  names: string[];
  isSpinning: boolean;
  onSpinComplete: (winner: string) => void;
}

export default function SpinWheel({ names, isSpinning, onSpinComplete }: SpinWheelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rotation, setRotation] = useState(0);
  const animationRef = useRef<number>();

  const colors = [
    '#FF6B6B', '#4ECDC4', '#FFD93D', '#6BCF7F', '#A78BFA',
    '#FF8C42', '#2EC4B6', '#FF5E78', '#FFC857', '#7B68EE',
    '#FF6F91', '#00D9FF', '#FFB627', '#5FD068', '#C77DFF'
  ];

  useEffect(() => {
    drawWheel();
  }, [names, rotation]);

  useEffect(() => {
    if (isSpinning) {
      startSpin();
    }
  }, [isSpinning]);

  const drawWheel = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = canvas.width;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = centerX - 16;

    ctx.clearRect(0, 0, size, size);

    if (names.length === 0) {
      // Empty state
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.fillStyle = '#f9fafb';
      ctx.fill();
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Dashed inner ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius - 30, 0, 2 * Math.PI);
      ctx.setLineDash([8, 6]);
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#9ca3af';
      ctx.font = '600 16px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Add names to start', centerX, centerY);
      return;
    }

    const anglePerSegment = (2 * Math.PI) / names.length;

    // Outer shadow ring
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + 4, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(0,0,0,0.04)';
    ctx.fill();

    // Draw segments
    names.forEach((name, index) => {
      const startAngle = rotation + index * anglePerSegment;
      const endAngle = startAngle + anglePerSegment;

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = colors[index % colors.length];
      ctx.fill();

      // Segment border
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Emoji (instead of text)
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(startAngle + anglePerSegment / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Larger emoji size
      const emojiSize = names.length > 8 ? 28 : names.length > 5 ? 32 : 36;
      ctx.font = `${emojiSize}px Arial, sans-serif`;

      const textRadius = radius * 0.65;
      ctx.fillText(name, textRadius, 0);
      ctx.restore();
    });

    // Center circle - outer ring
    ctx.beginPath();
    ctx.arc(centerX, centerY, 28, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = '#1f2937';
    ctx.fill();
  };

  const startSpin = () => {
    const spinDuration = 4200;
    const startTime = Date.now();
    const startRotation = rotation;
    const randomSpins = 5 + Math.random() * 4;
    const totalRotation = randomSpins * 2 * Math.PI + Math.random() * 2 * Math.PI;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / spinDuration, 1);

      // Cubic ease-out
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentRotation = startRotation + totalRotation * easeOut;

      setRotation(currentRotation);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        const normalizedRotation = currentRotation % (2 * Math.PI);
        const anglePerSegment = (2 * Math.PI) / names.length;
        const pointerAngle = (3 * Math.PI / 2) - normalizedRotation;
        const normalizedPointer = ((pointerAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const winnerIndex = Math.floor(normalizedPointer / anglePerSegment);
        const winner = names[winnerIndex];

        setTimeout(() => {
          onSpinComplete(winner);
        }, 400);
      }
    };

    animate();
  };

  return (
    <div className="relative flex items-center justify-center">
      {/* Pointer */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10" style={{ marginTop: '-2px' }}>
        <div className="w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[28px] border-t-gray-800 drop-shadow-md"></div>
      </div>

      {/* Wheel */}
      <canvas
        ref={canvasRef}
        width={480}
        height={480}
        className="w-full max-w-[420px] mx-auto"
      />
    </div>
  );
}
